/** /zh/decision-log — Chinese version of the decision-log landing page.
 *  Native zh content, same structure as DecisionLog.tsx. */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function DecisionLogZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/decision-log"
      hreflangLocales={['en', 'zh', 'zhtw']}
      documentTitle="决策日志（Decision Log）：不会腐烂的那一种 · Draftlize"
      documentDescription="大多数决策日志和 ADR 都会腐烂——写一次、没人回看、悄悄过时。Draftlize 让决策日志成为 AI agent 能读也能写的东西：一个决策一改，所有依赖它的决策自动标记 stale。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · 决策日志</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            大多数决策日志都会腐烂。<br/>
            <em>这是活的那一种。</em>
          </h1>
          <p className="lede">
            决策日志的作用，是在半年后回答一个问题：<em>「我们当时为什么这么定？」</em>可大多数日志做不到——写一次，埋进文档，然后在产品往前走的时候悄悄过时。这不是自律问题，而是一份静态文档根本无法知道：它记下的某个决策，什么时候已经不成立了。Draftlize 让决策日志成为 AI agent 能读也能写的东西——一个上游决策一变，每个依赖它的决策都会自动把自己标记成 stale。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">一份文档 vs <em>一份活的决策日志。</em></h2>
          </div>
          <p>写在 Notion、Confluence 或 markdown ADR 里的决策日志，是一张快照。决策一变，日志就错了，而且没人知道。解法不是更强的自律，而是一个替你追踪依赖的 substrate。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>文档 / Confluence / markdown ADR</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>记录一个决策</td><td className="ours yes">结构化卡片：决策、理由、备选、依赖</td><td className="partial">一段文字</td></tr>
            <tr><td>半年后还找得到</td><td className="ours yes">可按 URL / ID 寻址</td><td className="no">埋着，得在 wiki 里翻</td></tr>
            <tr><td>决策变了，下游知道吗</td><td className="ours yes">自动标记 stale</td><td className="no">没有连接，悄悄腐烂</td></tr>
            <tr><td>AI 用得上吗</td><td className="ours yes">经 MCP 原生读 + 写</td><td className="partial">顶多读个纯文本</td></tr>
            <tr><td>谁来保持一致</td><td className="ours yes">substrate 自动追踪</td><td className="no">全靠你的意志力</td></tr>
            <tr><td>和早先的决策冲突</td><td className="ours yes">自动浮现</td><td className="no">下次复盘才发现</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">为什么决策日志会<em>腐烂。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">写给一个永远不回来的读者</h3>
            <p>决策日志写给「未来的我们」。但未来的我们很忙，日志躺在没人重开的文档里。架构决策记录（ADR）从 2011 年就有，一直在腐烂，正是这个原因——读者是人，而人不会回头读。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">决策和它的依赖之间没有连接</h3>
            <p>你第 3 周定了定价，第 7 周改了。三个假设了旧定价的 spec 现在全错了——但没有东西把它们连到那个决策上，于是没人更新，直到演示时出事。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">维护靠手动，熵必然获胜</h3>
            <p>每一份决策日志都死于同一种死法：第一天完美，第九十天 40% 是错的。保持它最新，是个跟「发版」抢时间的杂活，而发版永远赢。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">一份<em>活的</em>决策日志做到了什么。</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">AI agent 是那个永不遗忘的读者</h3>
            <p>当日志的首要读者是 agent——一个在起草下一份 spec 前会读完每张相关卡片的 agent——决策日志就不再是坟场，而成了随手可得的上下文。它在每一轮都被读，而不是等到下次复盘。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">改一个决策，依赖自动标 stale</h3>
            <p>每个决策都是一张带显式依赖的卡片。改掉定价决策，每张依赖它的卡片自动变 stale——就像构建系统让一个改动文件的所有下游失效一样。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">每个决策都可寻址</h3>
            <p>决策是一张有稳定 ID 的卡片，能从 spec、thread、或经 MCP 从 Claude Code 引用。「依据决策 c-04」成了一个真实的链接，而不是模糊的记忆——而一份<a href="/zh/decision-log-template" style={{ color: 'var(--amber)' }}>决策日志模板</a>或<a href="/zh/architecture-decision-record" style={{ color: 'var(--amber)' }}>架构决策记录（ADR）</a>也成了你克隆的起手包，而不是让你发怵的空白页。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          决策日志只有在「有东西回头读它」时才有用。让那个东西，成为一个永不跳过任何一张卡片的 agent。
        </blockquote>
        <cite>你靠手动维持不下去的自律，substrate 替你维持。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '什么是决策日志？', a: '决策日志是团队重要决策的流水记录——定了什么、为什么、考虑过哪些备选、什么时候定的。它的作用是在几个月后回答「我们当时为什么这么定？」，让推理不至于丢失，旧的选择也不必每个 sprint 重新吵一遍。' },
            { q: '决策日志应该包含什么？', a: '每条应记下决策本身、背后的背景与问题、你权衡过的备选、理由、日期，以及依赖它的人或 spec。最后这一项是大多数日志漏掉的——而正是它，让你日后能知道：这个决策一变，什么会跟着坏。' },
            { q: '为什么决策日志会失效？', a: '因为一份静态文档无法知道：它记下的某个决策，什么时候已经不成立了。它写一次、埋进文档，然后在产品往前走时悄悄过时——又因为没有任何东西把决策连到依赖它的东西上，没人会去更新下游，直到出事。' },
            { q: 'Draftlize 怎么让决策日志保持最新？', a: 'Draftlize 把每个决策做成 AI agent 能读也能写的可寻址卡片。每张依赖卡都声明它靠什么，于是一个决策一变，所有下游自动标成 stale——你靠手动撑不住的那份自律，交给 substrate 替你撑。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">用 $5 <em>免费开始你的决策日志。</em></h2>
        </div>
        <div className="trust-strip">
          <span>注册送 $5</span>
          <span>按量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            建一个项目，用你已有的文档把它喂起来，让 agent 替你把决策——以及一切依赖它们的东西——保持最新。
          </p>
          <a className="btn-primary" href="/">用 $5 免费开始</a>
        </div>
      </section>
      <TemplateCta template="decision-log" locale="zh" />
    </MarketingShell>
  );
}
